import store from '@/store';
import { filter } from 'lodash';
import {
    UPLOAD_STATUS_WAITING,
    UPLOAD_STATUS_INITIALIZING,
    UPLOAD_STATUS_UPLOADING,
    UPLOAD_STATUS_UPLOADED,
    UPLOAD_STATUS_ERROR,
    defaultAttributes as defaultEpisodeAttributes,
} from '@/utils/config/episodeOptions';
import {
    validateTitle,
    validateDescription,
    validateEpisodeNo,
    validateSeasonNo,
    validateArtwork,
} from '@/utils/form/episodeValidation';

const episodeModal = new class {
    /**
     * Initializes modal with empty data.
     *
     * @param {object|null}  show  Show data.
     * @param {object}       data  Context of initialization.
     */
    setInitialData(show = null, data = {}) {
        this.updateModalState({
            ...{
                context: 'create',
                deferredStore: false,
                episode: defaultEpisodeAttributes,
                errors: {},
                file: null,
                saveStatus: null,
                show: {},
                uploadPercentage: 0,
                uploadStatus: UPLOAD_STATUS_WAITING,
                visible: false,
            },
            ...data,
            show,
        });
    }

    /**
     * Opens modal.
     */
    open() {
        this.updateModalState({ visible: true });
    }

    /**
     * Closes modal.
     */
    close() {
        this.updateModalState({ visible: false });
    }

    /**
     * Gets the data from episode modal.
     *
     * @param   {string}  key  Requested key.
     * @returns {*}            Value of request key.
     */
    getData(key) {
        return store.state.UIState.addEpisodeModal[key] ?? null;
    }

    /**
     * Gets all errors.
     *
     * @returns {object} All errors.
     */
    getErrors() {
        return this.getData('errors') || {};
    }

    /**
     * Gets error message for single field.
     *
     * @param   {string}      field  Field name.
     * @returns {string|null}        Error message.
     */
    getError(field) {
        return this.getErrors()[field] ?? null;
    }

    /**
     * Indicates whether given field has error.
     *
     * @param   {string}      field  Field name.
     * @returns {boolean}            Check result.
     */
    hasError(field) {
        return !! this.getError(field);
    }

    /**
     * Sets the list of all errors.
     *
     * @param {object} errors List of errors.
     */
    setErrors(errors) {
        this.updateModalState({ errors });
    }

    /**
     * Adds error for single field to list.
     *
     * @param {string} field   Field name.
     * @param {string} message Error message.
     */
    addError(field, message) {
        this.updateModalState({
            errors: {
                ...this.getErrors(),
                [field]: message,
            },
        });
    }

    /**
     * Clears error of single field.
     *
     * @param  {string} field Field name.
     */
    clearError(field) {
        this.updateModalState({
            errors: {
                ...this.getErrors(),
                [field]: null,
            },
        });
    }

    /**
     * Indicates whether modal is in edit context.
     *
     * @returns {boolean} Is modal in edit context?
     */
    inEditContext() {
        return 'edit' === this.getData('context');
    }

    /**
     * Indicates whether modal is in create context.
     *
     * @returns {boolean} Is modal in create context?
     */
    inCreateContext() {
        return 'create' === this.getData('context');
    }

    /**
     * Gets the episode object.
     *
     * @returns {object} Episode object.
     */
    getEpisode() {
        return this.getData('episode');
    }

    /**
     * Sets the episode object.
     *
     * @param {object} episode Episode object.
     */
    setEpisode(episode) {
        this.updateModalState({ episode });
    }

    /**
     * Updates the episode object.
     *
     * @param {object} data New episode data.
     */
    updateEpisode(data) {
        this.updateModalState({
            episode: {
                ...this.getEpisode(),
                ...data,
            },
        });
    }

    /**
     * Gets the data from episode by attribute key.
     *
     * @param   {string} key Field name.
     * @returns {*}          Field value.
     */
    getEpisodeData(key) {
        return this.getEpisode()[key] ?? null;
    }

    /**
     * Indicates whether episode exists (is stored in database) or not.
     *
     * @returns {boolean} Check result
     */
    episodeExists() {
        return !! this.getEpisodeData('id');
    }

    /**
     * Gets the audio upload status.
     *
     * @returns {number} Upload staus value.
     */
    getAudioFileUploadStatus() {
        return this.getData('uploadStatus');
    }

    /**
     * Gets the audio upload status.
     *
     * @param {number} status Upload staus value.
     */
    setAudioFileUploadStatus(status) {
        this.updateModalState({
            uploadStatus: status,
        });
    }

    /**
     * Indicates whether audio file input is waiting for file.
     *
     * @returns {boolean} Check value.
     */
    isAudioFileWaiting() {
        return this.getAudioFileUploadStatus() === UPLOAD_STATUS_WAITING;
    }

    /**
     * Indicates whether audio file input is initializing upload.
     *
     * @returns {boolean} Check value.
     */
    isAudioFileInitializing() {
        return this.getAudioFileUploadStatus() === UPLOAD_STATUS_INITIALIZING;
    }

    /**
     * Indicates whether audio file input is being uploaded.
     *
     * @returns {boolean} Check value.
     */
    isAudioFileUploading() {
        return this.getAudioFileUploadStatus() === UPLOAD_STATUS_UPLOADING;
    }

    /**
     * Indicates whether audio file has been uploaded.
     *
     * @returns {boolean} Check value.
     */
    isAudioFileUploaded() {
        return this.getAudioFileUploadStatus() === UPLOAD_STATUS_UPLOADED;
    }

    /**
     * Indicates whether an error occured during audio file upload.
     *
     * @returns {boolean} Check value.
     */
    isAudioFileHasError() {
        return this.getAudioFileUploadStatus() === UPLOAD_STATUS_ERROR;
    }

    /**
     * Sets the audio file of episode.
     *
     * @param {File} file Audio file.
     */
    setAudioFile(file) {
        this.updateModalState({ file });
        this.updateEpisode({
            fileName: file?.name ?? null,
            fileSize: file?.size ?? null,
            fileMimeType: file?.type ?? null,
        });
    }

    /**
     * Checkes whether episode has file.
     *
     * @returns {boolean} Check result.
     */
    hasAudioFile() {
        return this.getData('file') instanceof File || !! this.getEpisodeData('file');
    }

    /**
     * Gets the artwork file.
     *
     * @returns {string|File|null} Artwork file.
     */
    getArtworkFile() {
        return this.getEpisodeData('artwork');
    }

    /**
     * Sets the artwork file.
     *
     * @param {string|File|null} artwork Artwork file.
     */
    setArtworkFile(artwork) {
        this.updateEpisode({ artwork });
    }

    /**
     * Sets the upload progress of audio file.
     *
     * @param {number} progress Upload progress.
     */
    setAudioFileUploadProgress(progress) {
        this.updateModalState({ uploadPercentage: progress });
    }

    /**
     * Updates episode data in state.
     *
     * @param  {object} data Data to store.
     */
    updateModalState(data) {
        store.commit('changeUIState', {
            addEpisodeModal: {
                ...store.state.UIState.addEpisodeModal,
                ...data,
            },
        });
    }

    /**
     * Sets save status.
     *
     * @param {string} saveStatus Status of episode saving flow.
     */
    setSaveStatus(saveStatus) {
        this.updateModalState({ saveStatus });
    }

    /**
     * Indicates whether episode is being saved.
     *
     * @returns {boolean} Check result.
     */
    isSaving() {
        return 'saving' === this.getData('saveStatus');
    }

    /**
     * Indicates whether episode is saved.
     *
     * @returns {boolean} Check result.
     */
    isSaved() {
        return 'saved' === this.getData('saveStatus');
    }

    /**
     * Validates episode.
     *
     * @returns {boolean} Validation result.
     */
    async validate() {
        this.setErrors({
            title: validateTitle(this.getEpisodeData('title')),
            description: validateDescription(this.getEpisodeData('description')),
            episodeNo: validateEpisodeNo(this.getEpisodeData('episodeNo')),
            seasonNo: validateSeasonNo(this.getData('show')?.podcastType, this.getEpisodeData('seasonNo')),
            artwork: await validateArtwork(this.getEpisodeData('artwork'), {
                allowedMimeTypes: ['image/jpeg', 'image/png'],
            }),
        });

        return ! filter(this.getErrors(), error => null !== error).length;
    }

    /**
     * Changes state of deferring store.
     *
     * @param  {boolean} isDeferred Deffering store status.
     */
    deferStore(isDeferred = true) {
        this.updateModalState({ deferredStore: isDeferred });
    }

    /**
     * Checks whether store is deferred.
     *
     * @returns {boolean} Check result.
     */
    isStoreDeferred() {
        return true === this.getData('deferredStore');
    }
}();

export default episodeModal;
